import React, {Fragment, useEffect, useState} from "react";
import {Accordion, Button, Input, InputGroup, toaster} from "rsuite";
import { PrimaryClockApi } from "../../../../../services/PrimaryClock.api";
import NotificationFactory from "../../../../../shared/Notifications";
import UpdateButton from "../../../../shared/UpdateButton/UpdateButton";
import YandexMapComponent from "../../../../shared/YandexMap/YandexMap";
import {FaArrowDownIcon} from "../../../../../models/Icons.model";

interface CoordinatesBksProps {
  deviceId: string
}

interface MapCoordinates {
  latitude: string;
  longitude: string;
  latitudeDirection: string;
  longitudeDirection: string;
}

const CoordinatesBks = ({ deviceId }: CoordinatesBksProps) => {
  const [latitude, setLatitude] = useState<string>('');
  const [longitude, setLongitude] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [mapCoordinates, setMapCoordinates] = useState<MapCoordinates | null>(null);

  useEffect(() => {
    getCoordinates().then()
  }, [deviceId])

  const handleLatitudeChange = (value: string) => {
    setLatitude(value);
  };

  const handleLongitudeChange = (value: string) => {
    setLongitude(value);
  };

  const toMapString = (value: number | string) => {
    if (value === '' || value === null || value === undefined) return '';

    const num = Number(String(value).replace(',', '.'));
    if (Number.isNaN(num)) return '';

    const abs = Math.abs(num);
    const degrees = Math.floor(abs);
    const fraction = abs - degrees;

    const fraction4 = Math.round(fraction * 10000);

    const degreesStr = degrees.toString().padStart(3, '0');
    const fractionStr = fraction4.toString().padStart(4, '0');

    return `${degreesStr}.${fractionStr}`;
  };




  const getLatDir = (num: number) => (num >= 0 ? 'N' : 'S');
  const getLonDir = (num: number) => (num >= 0 ? 'E' : 'W');

  const getCoordinates = async () => {
    setLoading(true);

    try {
      const result = await PrimaryClockApi.getCoordinates(deviceId);

      const latNum = result?.lat ?? null;
      const lonNum = result?.lon ?? null;

      const latitude = latNum != null ? latNum.toFixed(6) : '';
      const longitude = lonNum != null ? lonNum.toFixed(6) : '';

      setLatitude(latitude);
      setLongitude(longitude);

      if (latNum != null && lonNum != null) {
        setMapCoordinates({
          latitude: toMapString(latNum),
          longitude: toMapString(lonNum),
          latitudeDirection: getLatDir(latNum),
          longitudeDirection: getLonDir(lonNum),
        });
      } else {
        setMapCoordinates(null);
      }
    } catch (error) {
      toaster.push(
        NotificationFactory.error("Ошибка"),
        { placement: "topEnd" }
      );
    } finally {
      setLoading(false);
    }
  };


  const handleMapUseCoordinates = (coordsFromMap: Record<string, string>) => {
    setLatitude(coordsFromMap?.latitude);
    setLongitude(coordsFromMap?.longitude);

    const latNum = Number(coordsFromMap?.latitude?.replace(',', '.'));
    const lonNum = Number(coordsFromMap?.longitude?.replace(',', '.'));

    if (!Number.isNaN(latNum) && !Number.isNaN(lonNum)) {
      setMapCoordinates({
        latitude: toMapString(latNum),
        longitude: toMapString(lonNum),
        latitudeDirection: getLatDir(latNum),
        longitudeDirection: getLonDir(lonNum),
      });
    } else {
      setMapCoordinates(null);
    }
  };



  const onUpdateCoordinates = async () => {
    setLoading(true);

    const lat = Number.parseFloat(latitude.replace(',', '.'));
    const lon = Number.parseFloat(longitude.replace(',', '.'));

    if (Number.isNaN(lat) || Number.isNaN(lon)) {
      toaster.push(
        NotificationFactory.error('Некорректные координаты'),
        { placement: 'topEnd' }
      );
      setLoading(false);
      return;
    }

    const coordinates = { lat, lon };

    try {
      await PrimaryClockApi.updateCoordinates(deviceId, coordinates);
      toaster.push(
        NotificationFactory.success('Успешно обновлено'),
        { placement: 'topEnd' }
      );
      getCoordinates().then();
    } catch (e) {
      console.error('Ошибка при сохранении координат:', e);
      toaster.push(
        NotificationFactory.error('Ошибка'),
        { placement: 'topEnd' }
      );
    } finally {
      setLoading(false);
    }
  };

  const onRecalculationCoordinates = async () => {
    try {
      setLoading(true)
      await PrimaryClockApi.recalculationCoordinates(deviceId);

      await getCoordinates().then();

      toaster.push(
        NotificationFactory.success('Успешно обновлено'),
        { placement: 'topEnd' }
      );

    } catch (e) {
      console.error('Ошибка при сохранении координат:', e);
      toaster.push(
        NotificationFactory.error('Ошибка'),
        { placement: 'topEnd' }
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="fieldset-wrapper mb-4">
        <div className="legend-title main-legend-title">Координаты БКС</div>
        <div className="row p-1">
          <div className="col-md-12 text-end">
            <UpdateButton loading={loading} onRefresh={onRecalculationCoordinates} tooltip={' Пересчет координат'}/>
          </div>
        </div>
        <div className="row p-1">
          <div className="col-md-3">
            <InputGroup>
              <InputGroup.Addon>Широта</InputGroup.Addon>
              <Input
                value={latitude || ''}
                onChange={handleLatitudeChange}
                style={{ textAlign: 'center' }}
              />
            </InputGroup>
          </div>
          <div className="col-md-3">
            <InputGroup>
              <InputGroup.Addon>Долгота</InputGroup.Addon>
              <Input
                value={longitude || ''}
                onChange={handleLongitudeChange}
                style={{ textAlign: 'center' }}
              />
            </InputGroup>
          </div>
          <div className="col-md-3">
            <Button
              style={{width: '100%'}}
              appearance="primary"
              loading={loading}
              onClick={onUpdateCoordinates}>
             Сохранить
            </Button>
          </div>
        </div>

        <div className="row p-2">
          <div className="col-md-12">

            <Fragment>
              <Accordion>
                <Accordion.Panel
                  caretAs={FaArrowDownIcon}
                  className="mb-2"
                  style={{padding: 0, border: '1px solid #ccc'}}
                  header="Выбор точки на карте">
                  <YandexMapComponent
                    inputCoordinates={mapCoordinates}
                    onUseCoordinates={handleMapUseCoordinates}
                  />
                </Accordion.Panel>
              </Accordion>
              </Fragment>
          </div>
        </div>
      </div>
    </>
  );
};

export default CoordinatesBks;
